// Default transaction-email sequence per side, using Team Edelstein's real
// templates (from "Chresta Email templates"). Pure data + helpers; no React /
// Firestore. ESM named exports so the production build resolves named imports.
//
// Tokens: {{propertyAddress}}, {{clientNames}}, {{sellerNames}}, {{buyerNames}},
// {{agentName}} auto-fill from the deal (via buildContext + interpolate).
// [Bracketed] items are left for the agent to fill in the draft before sending.
//
// Recipient roles: "client" | "lender" | "title" | "otherAgent" | "vendor".
// Editable later in the Phase-2 Settings manager.

const SIG = "\n\nBest regards,\n{{agentName}}";

// ---- Shared (used on both sides) ----
const TITLE_NEW_TRANSACTION = {
  id: "title-new-transaction",
  recipientRole: "title",
  recipientLabel: "Title",
  subject: "New Transaction: {{propertyAddress}}",
  body:
    "Hi [Closer],\n\n" +
    "I've attached the Purchase Agreement for {{agentName}}'s listing/buyers at {{propertyAddress}}. " +
    "The broker admin fee is $599/$575 and the commission is 6% (2.7% to the buyer's agent).\n\n" +
    "Sellers / Buyers: {{clientNames}}\n[Emails and phone numbers]\n" +
    "[Current address for buyers or sellers who don't live at the property]\n\n" +
    "Seller's / Buyer's closer: [Title company name] [Contact info]\n\nThanks,",
};

// ============================ SELLER ============================
const SELLER_SEQUENCE = [
  {
    key: "under-contract",
    label: "Under Contract",
    emails: [
      {
        id: "seller-offer-accepted",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Congrats on accepting an offer on {{propertyAddress}}!",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "Congratulations on accepting a new offer on {{propertyAddress}}! A copy of the fully executed " +
          "Purchase Agreement is attached for your records. Below you will find some information to get you " +
          "up to speed with the start of this transaction.\n\n" +
          "What's Happened:\n" +
          "• We've sent the Purchase Agreement to all necessary parties.\n" +
          "• The buyer's inspection is going to be scheduled and you should see a request come through ShowingTime. " +
          "Here is some basic information about the inspection:\n" +
          "   - They usually take between 2 to 4 hours to be conducted.\n" +
          "   - The inspector and buyer are typically present. The buyer's agent may accompany them and sometimes family members also attend.\n" +
          "   - As a homeowner, you should not be present at the time of the inspection.\n" +
          "   - Depending on what type of inspections the buyer decides to conduct, there may be multiple requests that come through ShowingTime. If there are any tests that need to be run (such as Radon) we will notify you about how to proceed.\n" +
          "   - The inspection contingency ends on [last day of contingency] at 11:59 PM.\n\n" +
          "What's Next:\n" +
          "• The buyer may or may not negotiate line items after their inspections are complete, at which time Henry will be in touch with you to discuss how you'd like to proceed. It can take up to 48 hours after the inspection for the buyer's agent to contact Henry about the results.\n" +
          "• We should see the earnest money deposited into our brokerage's trust account in the next couple of days, held until closing where it's credited to the buyer's down-payment.\n" +
          "• Please fill out the Information Transfer form in your Client Portal. We'll send this to the buyer's agent a couple days before closing so they have basic info about the house (trash day, garage code, etc).\n" +
          "• An appraisal will be ordered after the inspection and we'll keep in touch with the buyer's agent about the results.\n" +
          "• You will be contacted by Julie Davidson from Watermark Title with closing information. We'll check in with Julie in a couple of weeks to make sure everything is progressing smoothly.\n" +
          "• Updates will be communicated with you as they happen.\n\n" +
          "Lastly, we'll be sending you an Estimated Net Proceeds sheet to review and sign. These figures are not final but give you an idea of what you'll receive at closing. A few days before signing, Watermark Title will provide a preliminary Settlement Statement with the final numbers.\n\n" +
          "Any questions, just let us know." +
          SIG,
      },
      TITLE_NEW_TRANSACTION,
    ],
  },
  {
    key: "weekly-update",
    label: "Weekly Update",
    emails: [
      {
        id: "seller-weekly-update",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Weekly Update: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "As promised, here's this week's update on {{propertyAddress}} to keep you up to speed!\n\n" +
          "What's Happened:\n" +
          "• [What happened this week]\n\n" +
          "What's Next:\n" +
          "• [What's coming up]\n\n" +
          "Any questions, just let us know." +
          SIG,
      },
    ],
  },
  {
    key: "inspection",
    label: "Inspection",
    emails: [
      {
        id: "seller-inspection-scheduled",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Buyer's Inspection Scheduled: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "The buyer's inspection has been scheduled for [date] at [time]. You should see the request come " +
          "through ShowingTime. A few reminders for inspection day:\n" +
          "• Inspections usually take between 2 to 4 hours.\n" +
          "• The inspector and buyer are typically present, and sometimes the buyer's agent or family members attend.\n" +
          "• As the homeowner, you should not be present during the inspection.\n" +
          "• If additional tests are needed (such as Radon), we'll let you know how to proceed.\n\n" +
          "The inspection contingency ends on [last day of contingency] at 11:59 PM. We'll be in touch as soon " +
          "as we hear anything!" +
          SIG,
      },
      {
        id: "seller-inspection-results",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Inspection Update: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "The buyer's inspection on {{propertyAddress}} is complete. [Summary of the outcome, e.g., the buyer " +
          "is moving forward as-is / the buyer has requested the following items].\n\n" +
          "[If items were requested: Henry will be in touch to discuss how you'd like to proceed. Nothing needs " +
          "to be decided this minute. We'll walk through the options together.]\n\n" +
          "Any questions, just let us know." +
          SIG,
      },
    ],
  },
  {
    key: "financing",
    label: "Financing / Loan",
    emails: [
      {
        id: "seller-commitment-letter",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Commitment Letter Received: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "I wanted to let you know that we received the final commitment letter from the buyer's lender. " +
          "It is attached here for your records. Please let us know if you have any questions!" +
          SIG,
      },
    ],
  },
  {
    key: "appraisal",
    label: "Appraisal",
    emails: [
      {
        id: "seller-appraisal-waiver",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Appraisal Waiver Issued: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "The lender has issued an appraisal waiver for the buyer's purchase. This means the buyer's loan " +
          "will not be subject to an appraisal. That's another milestone passed on the way to closing. We'll " +
          "check in with the buyer's lender further down the road to make sure everything is in order for closing.\n\n" +
          "Any questions, just let us know." +
          SIG,
      },
      {
        id: "seller-appraisal-results",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Appraisal Results: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "The appraisal came back at an acceptable value for the purchase price. That's another milestone " +
          "passed on the way to closing. We'll continue checking in with the lender to make sure everything " +
          "is progressing smoothly.\n\n" +
          "Any questions, just let us know." +
          SIG,
      },
    ],
  },
  {
    key: "clear-to-close",
    label: "Clear to Close",
    emails: [
      {
        id: "seller-clear-to-close",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Clear to Close: {{propertyAddress}}!",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "Great news! The buyer's lender has issued the Clear to Close. All contingencies are satisfied and " +
          "we are officially on the home stretch.\n\n" +
          "Closing is scheduled for [DATE] at [TIME] at [LOCATION]. Watermark Title will be in touch with your " +
          "final Settlement Statement a few days beforehand so you can review the final numbers.\n\n" +
          "We'll send closing-day reminders as we get closer. Congratulations on reaching this milestone!" +
          SIG,
      },
    ],
  },
  {
    key: "closing",
    label: "Closing",
    emails: [
      {
        id: "seller-final-walkthrough",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Final Walk-Through Scheduled: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "The buyers have scheduled their final walk-through for [date/time]. The purpose of the final " +
          "walk-through is to make sure the home is still in the same, acceptable condition as when they " +
          "originally saw it.\n\n" +
          "The final walk-through is attended by the buyer and the buyer's agent. Please plan to be away during " +
          "this time. They will double-check inspection items and ensure everything is in working order.\n\n" +
          "Please leave any additional keys, garage door openers, or notes for them on the kitchen counter and " +
          "their agent will grab them during this time and hand them to the buyers after closing.\n\n" +
          "Thanks again for all your hard work. Any questions, just let us know." +
          SIG,
      },
      {
        id: "seller-closing-reminder",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Closing Reminder: {{propertyAddress}}",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "Closing day is almost here! As a reminder, closing is on [DATE] at [TIME]. Please make sure all items " +
          "have been removed from the home and the home is clean prior to closing. Leave any extra keys and garage " +
          "door openers in the kitchen. Also remember to cancel all utilities for the day of closing. I've attached " +
          "a list of utility companies in [CITY].\n\n" +
          "Please send us your new address so we can ensure we have your updated contact information on file. " +
          "Let us know if you have any questions!" +
          SIG,
      },
    ],
  },
  {
    key: "closed",
    label: "Closed",
    emails: [
      {
        id: "seller-closed-congrats",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Congratulations: {{propertyAddress}} Is Officially Sold! 🎉",
        body:
          "{{greeting}} {{sellerNames}},\n\n" +
          "Congratulations! The sale of {{propertyAddress}} is officially closed. It has truly been a pleasure " +
          "working with you, and we're so happy we could help you through this milestone.\n\n" +
          "A couple of final housekeeping items:\n" +
          "• Keep an eye out for your closing documents and proceeds per the arrangements made with the title company.\n" +
          "• If you haven't already, make sure utilities are canceled or transferred as of the closing date.\n\n" +
          "Even though this transaction is complete, we're never more than a phone call away, whether it's for you, your " +
          "family, or anyone you know who could use our help. Thank you again for trusting Team Edelstein!" +
          SIG,
      },
    ],
  },
];

// ============================ BUYER ============================
const BUYER_SEQUENCE = [
  {
    key: "under-contract",
    label: "Under Contract",
    emails: [
      {
        id: "buyer-offer-accepted",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Congrats: your offer on {{propertyAddress}} was accepted!",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "Congratulations on your offer being accepted! This step marks your home search ending and the " +
          "Contract to Close process beginning. We'll be sending you frequent updates with timeline check-ins " +
          "and other relevant information. I've attached a copy of the Purchase Agreement for your records.\n\n" +
          "EARNEST MONEY\n" +
          "There are two options for submitting your Earnest Money: drop your check at our office one business " +
          "day after final acceptance, or pay electronically via TrustFunds. We have 2 business days to get the " +
          "payment to the Listing Broker, so please submit as soon as possible. The listing brokerage will deposit " +
          "it within 3 business days of receipt and hold it until closing, when it's credited to your down payment.\n" +
          "TrustFunds: You should receive an email with a link to submit your Earnest Money (check your spam folder). " +
          "There is a security feature requiring a secret word on the payment form. We'll text that to you.\n\n" +
          "INSPECTION CONTINGENCY: ends [date] at 11:59 PM\n" +
          "We are scheduling the inspection with [Inspector]. He will conduct the inspection on [date] with a " +
          "walk-through with you and Henry at [time]. He arrives 2 hours before your walk-through to inspect the " +
          "home; your educational walk-through takes at least 1 hour, and he'll email you a complete report with " +
          "photos. An inspection typically lists at least 30-40 items of routine maintenance or suggested improvements. " +
          "If severe problems are found, your contingency permits you to cancel within the timelines in the Purchase " +
          "Agreement, or you may negotiate repairs.\n\n" +
          "NEXT STEPS\n" +
          "You will be contacted by Julie Davidson from Watermark Title with closing information. We've sent your " +
          "lender a copy of your executed purchase agreement; they'll be in touch with next steps and order the " +
          "appraisal once we're through the Inspection Contingency (usually received within 2 weeks of being ordered).\n" +
          "During the mortgage process, please: provide requested documents promptly, do not open any new lines of " +
          "credit, and do not make any large purchases.\n\n" +
          "Please send us your current address so we have the correct contact information on file. If you have any " +
          "questions or concerns, please let us know!" +
          SIG,
      },
      TITLE_NEW_TRANSACTION,
    ],
  },
  {
    key: "earnest-money",
    label: "Earnest Money",
    emails: [
      {
        id: "buyer-earnest-money",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Earnest Money Received: {{propertyAddress}}",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "Great news! Your Earnest Money of [amount] has been received and deposited into the listing " +
          "brokerage's trust account. It will be held there until closing, when it is credited toward your " +
          "down payment.\n\n" +
          "That's one more item checked off the list. Any questions, just let us know." +
          SIG,
      },
    ],
  },
  {
    key: "inspection",
    label: "Inspection",
    emails: [
      {
        id: "buyer-inspection-scheduled",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Your Inspection Is Scheduled: {{propertyAddress}}",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "Your inspection on {{propertyAddress}} is scheduled for [date] with [Inspector]. He will arrive about " +
          "2 hours early to inspect the home, and your educational walk-through with Henry starts at [time]. " +
          "Plan for it to take at least an hour.\n\n" +
          "A reminder on what to expect: the inspector will email you a complete report with photos, and a " +
          "typical inspection lists 30-40 items of routine maintenance or suggested improvements. That's normal! " +
          "If anything significant comes up, we'll walk through your options together.\n\n" +
          "Your inspection contingency ends on [date] at 11:59 PM. See you at the walk-through!" +
          SIG,
      },
      {
        id: "buyer-inspection-results",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Inspection Results & Next Steps: {{propertyAddress}}",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "Thanks for attending the inspection walk-through! Now that the report is in hand, here's where things " +
          "stand: [summary of findings].\n\n" +
          "Your options within the inspection contingency (which ends [date] at 11:59 PM):\n" +
          "• Move forward as-is,\n" +
          "• Ask the sellers to address specific items, or\n" +
          "• Cancel within the contingency timelines if the findings are severe.\n\n" +
          "Henry will be in touch to talk through what makes the most sense. If any additional tests were run " +
          "(such as Radon), we'll forward those results as soon as they arrive." +
          SIG,
      },
    ],
  },
  {
    key: "financing",
    label: "Financing / Loan",
    emails: [
      {
        id: "buyer-loan-status-update",
        recipientRole: "lender",
        recipientLabel: "Lender",
        subject: "Loan Status Update: {{propertyAddress}}",
        body:
          "{{greeting}} [LO Name],\n\n" +
          "I wanted to check in to see where we are in the loan process for {{propertyAddress}} (Buyers: {{buyerNames}}). " +
          "Do you have any updates? Please let me know if there is anything you need from us." +
          SIG,
      },
      {
        id: "buyer-written-statement-due",
        recipientRole: "lender",
        recipientLabel: "Lender",
        subject: "Written Statement Due Today: {{propertyAddress}}",
        body:
          "{{greeting}} [LO Name],\n\n" +
          "We wanted to follow up with a reminder about the written statement for {{propertyAddress}} (Buyers: {{buyerNames}}) " +
          "that is due today. Please send over the final written statement when you get a chance. Thank you!" +
          SIG,
      },
      {
        id: "buyer-financing-deadline-reminder",
        recipientRole: "lender",
        recipientLabel: "Lender",
        subject: "Financing Deadline Reminder: {{propertyAddress}}",
        body:
          "{{greeting}} [LO Name],\n\n" +
          "We wanted to follow up with a courtesy reminder about the written statement for {{propertyAddress}} (Buyers) " +
          "due [date]. Please let us know if you have any questions. Thank you in advance!" +
          SIG,
      },
      {
        id: "buyer-ff-statement-reminder",
        recipientRole: "lender",
        recipientLabel: "Lender",
        subject: "Written Statement Reminder: {{propertyAddress}}",
        body:
          "Hi [Lender],\n\n" +
          "Thank you for the update! Per the purchase agreement, we will still need a written statement " +
          "[today / by tomorrow / whatever day it is due]." +
          SIG,
      },
      {
        id: "buyer-commitment-letter-to-agent",
        recipientRole: "otherAgent",
        recipientLabel: "Other Agent",
        subject: "Buyer Commitment Letter: {{propertyAddress}}",
        body:
          "{{greeting}} [Agent],\n\n" +
          "The commitment letter from the buyers' lender is attached." +
          SIG,
      },
      {
        id: "buyer-home-warranty",
        recipientRole: "vendor",
        recipientLabel: "Home Warranty",
        subject: "Home Warranty Request: {{propertyAddress}}",
        body:
          "Hi Jennifer,\n\n" +
          "We need a home warranty for our buyers at {{propertyAddress}}.\n\n" +
          "Buyers: {{buyerNames}}\nClosing: [Date]\n\nThanks,",
      },
    ],
  },
  {
    key: "appraisal",
    label: "Appraisal",
    emails: [
      {
        id: "buyer-lo-appraisal-order",
        recipientRole: "lender",
        recipientLabel: "Lender",
        subject: "Appraisal Order Check: {{propertyAddress}}",
        body:
          "{{greeting}} [LO Name],\n\n" +
          "I wanted to check in to see if the appraisal has been ordered for {{propertyAddress}} (Buyers: {{buyerNames}}). " +
          "Can you please let us know the current status of the appraisal and when it is due back?" +
          SIG,
      },
      {
        id: "buyer-lo-appraisal-results",
        recipientRole: "lender",
        recipientLabel: "Lender",
        subject: "Appraisal Results Check: {{propertyAddress}}",
        body:
          "{{greeting}} [LO Name],\n\n" +
          "I am following up about the results of the appraisal for {{propertyAddress}} (Buyers: {{buyerNames}}) that " +
          "was done on [Appraisal date]. Please let me know if you have the results back or when you expect to get them. Thank you!" +
          SIG,
      },
      {
        id: "buyer-appraisal-results",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Appraisal Results: {{propertyAddress}}",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "The appraisal came back at an acceptable value for the purchase price. That's another milestone " +
          "passed on the way to closing. Continue checking in with your loan officer to make sure everything is " +
          "progressing smoothly.\n\n" +
          "Any questions, just let us know." +
          SIG,
      },
    ],
  },
  {
    key: "clear-to-close",
    label: "Clear to Close",
    emails: [
      {
        id: "buyer-clear-to-close",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "You're Clear to Close: {{propertyAddress}}!",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "Fantastic news! Your lender has issued the Clear to Close. Your financing is fully approved and we " +
          "are officially on the home stretch.\n\n" +
          "Closing is scheduled for [DATE] at [TIME] at [LOCATION]. A few reminders as we approach the big day:\n" +
          "• Watermark Title will send your final closing figures a few days beforehand.\n" +
          "• IMPORTANT: If you're wiring funds, call the title company directly at a number you trust to verify " +
          "wire instructions. Never trust wiring instructions received by email. Wire fraud is real.\n" +
          "• Bring a valid photo ID for each buyer to closing.\n" +
          "• Continue to avoid new credit lines or large purchases until after closing.\n\n" +
          "Almost there. Congratulations!" +
          SIG,
      },
    ],
  },
  {
    key: "closing",
    label: "Closing",
    emails: [
      {
        id: "buyer-final-walkthrough",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Final Walk-Through: {{propertyAddress}}",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "Your final walk-through is scheduled for [date/time]. This is our chance to make sure the home is in " +
          "the same, acceptable condition as when you made your offer. We'll double-check any inspection items " +
          "and ensure everything is in working order.\n\n" +
          "The sellers will leave any additional keys, garage door openers, and notes for you; we'll collect them " +
          "at the walk-through and hand everything over after closing.\n\n" +
          "If anything looks off, we'll address it before the closing table. See you there!" +
          SIG,
      },
      {
        id: "buyer-closing-statement",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Closing Day Details: {{propertyAddress}}",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "Closing day is almost here! As a reminder, closing is on [DATE] at [TIME] at the Team Edelstein office " +
          "(757 Snelling Avenue S). I've attached a PDF with parking information. Please watch the video below for a " +
          "few friendly reminders!\n\n" +
          "Remember to bring all documents and ID required by the title company to closing. If you have questions on " +
          "what to bring, please contact Julie Davidson with Watermark Title. If you are bringing cash to closing over " +
          "$2,000, please ensure it is a certified or cashier's check.\n\n" +
          "Lastly, please remember to switch over all utilities for the day of closing. I've attached a list of utility " +
          "companies in [CITY]. Should you have any questions, please let us know!" +
          SIG,
      },
    ],
  },
  {
    key: "closed",
    label: "Closed",
    emails: [
      {
        id: "buyer-closed-congrats",
        recipientRole: "client",
        recipientLabel: "Client",
        subject: "Welcome Home: Congratulations! 🎉",
        body:
          "{{greeting}} {{buyerNames}},\n\n" +
          "CONGRATULATIONS! {{propertyAddress}} is officially yours. It has been an absolute pleasure helping " +
          "you find and close on your new home.\n\n" +
          "A few final items as you settle in:\n" +
          "• Make sure utilities are switched into your name as of the closing date.\n" +
          "• Keep your closing documents somewhere safe. You'll want them at tax time.\n" +
          "• Consider filing for homestead on your new property.\n\n" +
          "Even though the transaction is complete, we're never more than a phone call away, whether it's for you, your " +
          "family, or anyone you know who could use our help. Welcome home, and thank you for trusting Team " +
          "Edelstein!" +
          SIG,
      },
    ],
  },
];

// Which sequence a deal follows. Deals normally store dealtype "Buyer"/"Seller",
// but imported/bulk-uploaded records have shipped variants like "buyer" or "" —
// normalize instead of silently falling back to the seller list.
export function sequenceForSide(side) {
  const isBuyer = String(side || "").trim().toLowerCase().startsWith("buy");
  return isBuyer ? BUYER_SEQUENCE : SELLER_SEQUENCE;
}

export function allEmailIds(side) {
  return sequenceForSide(side).flatMap((m) => m.emails.map((e) => e.id));
}

export function emailStatus(deal, emailId) {
  const te = (deal && deal.transactionEmails) || {};
  if (te.sent && te.sent[emailId]) return { state: "sent", record: te.sent[emailId] };
  if (te.skipped && te.skipped[emailId]) return { state: "skipped" };
  return { state: "pending" };
}

export function countSent(deal, emailIds) {
  const sent = (deal && deal.transactionEmails && deal.transactionEmails.sent) || {};
  return (emailIds || []).filter((id) => sent[id]).length;
}

// ── Editor integration (Settings → Email Templates) ─────────────────────
// Flat map of every transaction email's default subject/body, keyed by id —
// lets the shared template editor + the deal panels resolve a default and
// layer saved customizations on top. First occurrence wins on shared ids.
export const DEFAULT_TRANSACTION_TEMPLATES = (() => {
  const map = {};
  for (const seq of [SELLER_SEQUENCE, BUYER_SEQUENCE])
    for (const m of seq)
      for (const e of m.emails)
        if (!map[e.id]) map[e.id] = { subject: e.subject, body: e.body };
  return map;
})();

const _humanize = (id) =>
  id.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

// Picker metadata per transaction email id: side category, a readable label,
// and a one-line meta (stage · recipient). Consumed by emailTemplateLoader.
export const TRANSACTION_TEMPLATE_META = (() => {
  const meta = {};
  const sides = [
    ["Seller", SELLER_SEQUENCE],
    ["Buyer", BUYER_SEQUENCE],
  ];
  for (const [side, seq] of sides)
    for (const m of seq)
      for (const e of m.emails)
        if (!meta[e.id])
          meta[e.id] = {
            category: `Transaction · ${side}`,
            label: _humanize(e.id),
            meta: `${m.label} · ${e.recipientLabel}`,
          };
  return meta;
})();
